import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CartRepository } from '@asm/features/sync/repositories/cart.repository';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { BankRepository } from '@asm/features/sync/repositories/bank.repository';
import { DocumentTypeRepository } from '@asm/features/sync/repositories/document-type.repository';
import { FreightTypeRepository } from '@asm/features/sync/repositories/freight-type.repository';
import { PaymentConditionRepository } from '@asm/features/sync/repositories/payment-condition.repository';
import { PaymentMethodRepository } from '@asm/features/sync/repositories/payment-method.repository';
import { WarehouseRepository } from '@asm/features/sync/repositories/warehouse.repository';
import { Cart, CartEntry, MoneyValue } from '@asm/features/quick-order/models/cart.model';
import { QuickOrderProduct } from '@asm/features/quick-order/models/quick-order-search.model';
import { ManualCondition, PricingResult } from '@asm/features/sync/services/pricing/pricing.interfaces';
import {
  DefaultLocalPricingService,
  serializePricingSteps,
} from '@asm/features/sync/services/pricing/default-local-pricing.service';
import { OfflineHelpersService } from '@asm/features/sync/interceptor/offline-helpers.service';
import { T685Repository } from '@asm/features/sync/repositories/t685.repository';
import { PromotionRepository } from '@asm/features/sync/repositories/promotion.repository';
import { DefaultLocalPromotionEngineService } from '@asm/features/sync/services/promotion/default-local-promotion-engine.service';
import { generateUUID } from '@shared/helpers/generate-uuid';
import { CodedEntity } from '@asm/features/sync/models/coded-entity.model';
import { ASM_OFFLINE_CART_CODE_KEY, ASM_UNIT_DETAILS_KEY, STORE_CONFIG_KEY } from '@shared/tools/storage-keys';

const DISCOUNT_CONDITION_CODE = 'ZPE1';
const SURCHARGE_CONDITION_CODE = 'ZAMP';
const PAYMENT_CONDITION_CODE = 'ZCPG';

/**
 * Serviço que centraliza toda a lógica de negócio de carrinho offline:
 * get-or-create, get, add entry, patch entry, delete entry, patch cart,
 * recalcular totais e aplicar descrições de referência.
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalCartService {
  private readonly helper = inject(OfflineHelpersService);
  private readonly cartRepo = inject(CartRepository);
  private readonly productRepo = inject(ProductRepository);
  private readonly warehouseRepo = inject(WarehouseRepository);
  private readonly documentTypeRepo = inject(DocumentTypeRepository);
  private readonly freightTypeRepo = inject(FreightTypeRepository);
  private readonly paymentMethodRepo = inject(PaymentMethodRepository);
  private readonly paymentConditionRepo = inject(PaymentConditionRepository);
  private readonly bankRepo = inject(BankRepository);
  private readonly t685Repo = inject(T685Repository);
  private readonly promotionRepo = inject(PromotionRepository);
  private readonly promotionEngineService = inject(DefaultLocalPromotionEngineService);
  private readonly localPricingService = inject(DefaultLocalPricingService);

  // ─── Operações de carrinho ────────────────────────────────────────────────

  /**
   * Chave usada para persistir o código do carrinho offline no localStorage,
   * permitindo restaurá-lo corretamente ao retornar para o modo offline.
   */
  static readonly OFFLINE_CART_CODE_KEY = ASM_OFFLINE_CART_CODE_KEY;

  async getOrCreate(b2bUnitId: string): Promise<Cart> {
    // Tenta restaurar o carrinho offline pelo código salvo na sessão anterior
    const savedCode = localStorage.getItem(DefaultLocalCartService.OFFLINE_CART_CODE_KEY);
    let cart: Cart | undefined | null = null;

    if (savedCode) {
      cart = await firstValueFrom(this.cartRepo.getByKey(savedCode));
    }

    // Fallback: qualquer carrinho ativo no store
    if (!cart) {
      cart = await firstValueFrom(this.cartRepo.getActive());
    }

    if (!cart) {
      cart = this.applyDefaultsFromStorage(this.createEmptyCart());
    } else if ((cart.entries?.length ?? 0) > 0) {
      cart = await this.recalculateCart(cart, b2bUnitId);
    }

    // Persiste o código para ser encontrado na próxima entrada no modo offline
    localStorage.setItem(DefaultLocalCartService.OFFLINE_CART_CODE_KEY, cart.code);
    await firstValueFrom(this.cartRepo.addOrUpdate(cart));
    return cart;
  }

  async getCart(cartCode: string, b2bUnitId: string): Promise<Cart | null> {
    const storedCart = await firstValueFrom(this.cartRepo.getByKey(cartCode));
    const cart =
      storedCart && (storedCart.entries?.length ?? 0) > 0
        ? await this.recalculateCart(storedCart, b2bUnitId)
        : storedCart;

    if (cart) {
      await firstValueFrom(this.cartRepo.addOrUpdate(cart));
    }

    return cart ?? null;
  }

  async addEntry(body: any, b2bUnitId: string): Promise<Cart> {
    let cart = await firstValueFrom(this.cartRepo.getActive());
    if (!cart) {
      cart = this.createEmptyCart();
    }

    const product = await firstValueFrom(this.productRepo.getByKey(body.productCode));
    const entryNumber = cart.entries.length;
    const qty = Number(body.qty ?? 0);

    cart.entries = [...cart.entries, this.createCartEntry(product as any, body, entryNumber, qty)];
    cart = await this.recalculateCart(cart, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(cart));
    return cart;
  }

  async patchEntry(cartUrl: string, entryNumber: number, body: any, b2bUnitId: string): Promise<Cart> {
    const cart = await this.getCartFromUrl(cartUrl);
    if (!cart) throw new Error('Nenhum carrinho ativo');

    const idx = cart.entries.findIndex((entry: CartEntry) => entry.entryNumber === entryNumber);
    if (idx >= 0) {
      cart.entries[idx] = {
        ...cart.entries[idx],
        qty: Number(body.qty ?? cart.entries[idx].qty),
        warehouseCode: body.warehouseCode ?? body.warehouse ?? cart.entries[idx].warehouseCode,
        discountPercent: body.discount ?? cart.entries[idx].discountPercent,
        surchargePercent: body.surcharge ?? cart.entries[idx].surchargePercent,
        observation: body.observation ?? cart.entries[idx].observation,
        stockAllocations: body.stockAllocations ?? cart.entries[idx].stockAllocations,
        excludedPromotions:
          body.qty != null && Number(body.qty) !== (cart.entries[idx].qty ?? 0)
            ? []
            : (cart.entries[idx].excludedPromotions ?? []),
      };
    }

    const recalculated = await this.recalculateCart(cart, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    return recalculated;
  }

  async deleteEntry(cartUrl: string, entryNumber: number, b2bUnitId: string): Promise<Cart> {
    const cart = await this.getCartFromUrl(cartUrl);
    if (!cart) throw new Error('Nenhum carrinho ativo');

    cart.entries = cart.entries.filter((entry: CartEntry) => entry.entryNumber !== entryNumber);
    const recalculated = await this.recalculateCart(cart, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    return recalculated;
  }

  /**
   * Remove uma promoção de um entry específico adicionando o `sapConditionType`
   * à lista `excludedPromotions` do entry e recalculando o carrinho.
   *
   * Espelha `DefaultCartService.removePromotion()` do backend.
   */
  async removePromotion(cartUrl: string, entryNumber: number, promotionCode: string, b2bUnitId: string): Promise<Cart> {
    const cart = await this.getCartFromUrl(cartUrl);
    if (!cart) throw new Error('Nenhum carrinho ativo');

    const idx = cart.entries.findIndex((e: CartEntry) => e.entryNumber === entryNumber);
    if (idx < 0) throw new Error(`Entry ${entryNumber} nao encontrado`);

    const entry = cart.entries[idx];
    const current = entry.excludedPromotions ?? [];
    if (!current.includes(promotionCode)) {
      cart.entries[idx] = { ...entry, excludedPromotions: [...current, promotionCode] };
    }

    const recalculated = await this.recalculateCart(cart, b2bUnitId);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    return recalculated;
  }

  async patchCart(cartUrl: string, patch: any, b2bUnitId: string): Promise<Cart> {
    const cart = await this.getCartFromUrl(cartUrl);
    if (!cart) throw new Error('Nenhum carrinho ativo');

    let updated = { ...cart, ...patch } as Cart;

    updated = await this.applyReferenceDescriptions(updated);

    const shouldRecalculate =
      patch != null &&
      typeof patch === 'object' &&
      ['paymentMethodCode', 'paymentConditionCode', 'fullDiscount'].some(
        (field: string) => field in (patch as Record<string, unknown>),
      );

    if (shouldRecalculate) {
      updated = await this.recalculateCart(updated, b2bUnitId);
    }

    await firstValueFrom(this.cartRepo.addOrUpdate(updated));
    return updated;
  }

  // ─── Helpers de recalculo e descrições ───────────────────────────────────

  /**
   * Recalcula todos os totais do carrinho usando o serviço de precificação local.
   * Integra o motor de promoções offline: as `ManualCondition[]` das promoções
   * qualificadas são mescladas com as condições manuais de desconto/acréscimo.
   */
  async recalculateCart(cart: Cart, b2bUnitId: string): Promise<Cart> {
    let totalFactoryPrice = 0;
    let totalPrice = 0;
    let totalDiscounts = 0;
    let totalTax = 0;
    let totalIpi = 0;
    let totalSt = 0;
    let totalIcms = 0;

    // Carrega T685 uma única vez para toda a iteração de entries
    const allT685 = await firstValueFrom(this.t685Repo.getAll());

    // Carrega a condição de pagamento do carrinho uma única vez para aplicar
    // desconto/acréscimo (ZCPG) em todos os itens — espelha o backend
    const paymentConditionEntity = cart.paymentConditionCode
      ? await firstValueFrom(this.paymentConditionRepo.getByKey(cart.paymentConditionCode))
      : undefined;
    const paymentConditionManualConditions = this.buildPaymentConditionManualConditions(paymentConditionEntity);

    const recalculatedEntries = await Promise.all(
      (cart.entries ?? []).map(async (entry: CartEntry) => {
        const productCode = entry.productCode ?? '';
        const qty = entry.qty ?? 0;
        const product = productCode ? await firstValueFrom(this.productRepo.getByKey(productCode)) : undefined;
        const offlineImageUrls = product?.mediaUrls?.filter(url => Boolean(String(url ?? '').trim())) ?? [];

        let pricing: PricingResult;
        try {
          const manualConditions = this.buildManualConditions(entry.discountPercent ?? 0, entry.surchargePercent ?? 0);
          const promoConditions = await this.promotionEngineService.getPromotionConditions(
            productCode,
            b2bUnitId,
            cart,
            entry,
            allT685,
          );
          pricing = await this.localPricingService.getPrice(
            productCode,
            qty,
            b2bUnitId,
            entry.warehouseCode ?? undefined,
            [...manualConditions, ...promoConditions, ...paymentConditionManualConditions],
          );
        } catch {
          pricing = this.createEmptyPricingResult();
        }

        const ipiValue = Number((pricing.ipi * qty).toFixed(2));
        const stValue = Number((pricing.st * qty).toFixed(2));
        const icmsValue = Number((pricing.icms * qty).toFixed(2));
        const entryTaxValue = Number(pricing.totalTax.toFixed(2));

        totalFactoryPrice += pricing.baseTotal;
        totalPrice += pricing.finalTotal;
        totalDiscounts += pricing.totalDiscount;
        totalTax += entryTaxValue;
        totalIpi += ipiValue;
        totalSt += stValue;
        totalIcms += icmsValue;

        return {
          ...entry,
          imageUrls: offlineImageUrls.length > 0 ? offlineImageUrls : entry.imageUrls,
          totalWeight: Number(((entry.grossWeigh ?? 0) * qty).toFixed(2)),
          basePrice: this.helper.moneyValue(pricing.baseValue),
          totalPrice: this.helper.moneyValue(pricing.finalTotal),
          discount: this.helper.moneyValue(pricing.totalDiscount),
          totalDiscount: this.helper.moneyValue(pricing.totalDiscount),
          surcharge: this.helper.moneyValue(pricing.totalSurcharge),
          totalTax: this.helper.moneyValue(entryTaxValue),
          ipi: this.helper.moneyValue(ipiValue),
          st: this.helper.moneyValue(stValue),
          icms: this.helper.moneyValue(icmsValue),
          // Serialize pricing steps for price formation component (mirrors online)
          conditions: serializePricingSteps(pricing.steps, pricing.currency),
        };
      }),
    );

    // Preenche couponMessage se houver cupom ativo e uma promoção de cupom qualificada
    let couponMessage: string | undefined = cart.couponMessage;
    if (cart.couponCode) {
      try {
        const activePromotions = await firstValueFrom(this.promotionRepo.getActivePromotions());
        const couponPromo = activePromotions.find(p => !p.applyAutomatically && p.code === cart.couponCode);
        couponMessage = couponPromo?.message ?? undefined;
      } catch {
        // best effort
      }
    } else {
      couponMessage = undefined;
    }

    return {
      ...cart,
      entries: recalculatedEntries,
      couponMessage,
      totalFactoryPrice: Number(totalFactoryPrice.toFixed(2)),
      totalPrice: Number(totalPrice.toFixed(2)),
      totalDiscounts: Number(totalDiscounts.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      totalIpi: Number(totalIpi.toFixed(2)),
      totalSt: Number(totalSt.toFixed(2)),
      totalICMS: Number(totalIcms.toFixed(2)),
      totalIPIFormatted: this.helper.formatCurrency(totalIpi),
      totalSTFormatted: this.helper.formatCurrency(totalSt),
      totalICMSFormatted: this.helper.formatCurrency(totalIcms),
      totalItems: recalculatedEntries.reduce((sum, entry) => sum + (entry.qty ?? 0), 0),
      totalWeight: Number(
        recalculatedEntries.reduce((sum, entry) => sum + Number(entry.totalWeight ?? 0), 0).toFixed(2),
      ),
      calculated: true,
    };
  }

  /**
   * Enriquece o carrinho com as descrições dos dados de referência.
   */
  async applyReferenceDescriptions(cart: Cart): Promise<Cart> {
    const [warehouses, documentTypes, freightTypes, paymentMethods, paymentConditions, banks] = await Promise.all([
      firstValueFrom(this.warehouseRepo.getAll()),
      firstValueFrom(this.documentTypeRepo.getAll()),
      firstValueFrom(this.freightTypeRepo.getAll()),
      firstValueFrom(this.paymentMethodRepo.getAll()),
      firstValueFrom(this.paymentConditionRepo.getAll()),
      firstValueFrom(this.bankRepo.getAll()),
    ]);

    return {
      ...cart,
      warehouseDescription: this.helper.findDescription(warehouses, cart.warehouseCode),
      documentTypeDescription: this.helper.findDescription(documentTypes, cart.documentTypeCode),
      freightTypeDescription: this.helper.findDescription(freightTypes, cart.freightTypeCode),
      paymentMethodDescription: this.helper.findDescription(paymentMethods, cart.paymentMethodCode),
      paymentConditionDescription: this.helper.findDescription(paymentConditions, cart.paymentConditionCode),
      bankDescription: this.helper.findDescription(banks, cart.bankCode),
    };
  }

  /** Busca o carrinho pelo código na URL ou o carrinho ativo. */
  async getCartFromUrl(url: string): Promise<Cart | null> {
    const match = url.match(/\/cart\/([^/?]+)/);
    const cartCode = match?.[1] ?? null;

    if (cartCode) {
      const byKey = await firstValueFrom(this.cartRepo.getByKey(cartCode));
      if (byKey) return byKey;
    }

    return firstValueFrom(this.cartRepo.getActive());
  }

  /** Cria um carrinho vazio com totais zerados. */
  createEmptyCart(): Cart {
    return {
      code: generateUUID(),
      entries: [],
      calculated: true,
      totalPrice: 0,
      totalFactoryPrice: 0,
      totalDiscounts: 0,
      totalTax: 0,
      totalIpi: 0,
      totalSt: 0,
      totalICMS: 0,
      totalICMSFormatted: this.helper.formatCurrency(0),
      totalSTFormatted: this.helper.formatCurrency(0),
      totalIPIFormatted: this.helper.formatCurrency(0),
    };
  }

  /**
   * Lê os defaults de configuração do localStorage (app.store_config, asm.unit_details)
   * e os aplica ao carrinho, sem sobrescrever campos que já possuam valor.
   *
   * Chamado ao criar um carrinho vazio para garantir que documentTypeCode,
   * freightTypeCode, warehouseCode e outras propriedades padrão estejam
   * presentes desde o início — independente de o PATCH subsequente do
   * syncWithCart ser processado com sucesso.
   */
  private applyDefaultsFromStorage(cart: Cart): Cart {
    try {
      const storeConfigRaw = localStorage.getItem(STORE_CONFIG_KEY);
      const storeConfig = storeConfigRaw ? JSON.parse(storeConfigRaw) : null;

      const b2bDetailsRaw = localStorage.getItem(ASM_UNIT_DETAILS_KEY);
      const b2bDetails = b2bDetailsRaw ? JSON.parse(b2bDetailsRaw) : null;

      return {
        ...cart,
        documentTypeCode: cart.documentTypeCode || storeConfig?.defaultSalesDocumentTypeCode || undefined,
        freightTypeCode: cart.freightTypeCode || storeConfig?.defaultFreightTypeCode || undefined,
        warehouseCode: cart.warehouseCode || b2bDetails?.supplierCenter || undefined,
        paymentMethodCode: cart.paymentMethodCode || b2bDetails?.paymentMode || undefined,
        paymentConditionCode: cart.paymentConditionCode || b2bDetails?.paymentModeCondition || undefined,
      };
    } catch {
      return cart;
    }
  }

  /** Cria uma entrada de carrinho com valores zerados prontos para recalculação. */
  createCartEntry(
    product: Partial<QuickOrderProduct> | undefined,
    body: any,
    entryNumber: number,
    qty: number,
  ): CartEntry {
    const price: MoneyValue = { value: 0, formattedValue: this.helper.formatCurrency(0) };

    return {
      id: generateUUID(),
      entryNumber,
      productId: body.productCode,
      productCode: body.productCode,
      productEan: (product as any)?.sapEAN ?? product?.sapCode ?? '',
      productName: product?.name ?? product?.sapName ?? body.productCode,
      qty,
      warehouseCode: body.warehouse ?? body.warehouseCode ?? '',
      grossWeigh: Number((product as any)?.grossWeight ?? 0),
      basePrice: price,
      totalPrice: price,
      discount: this.helper.moneyValue(0),
      totalDiscount: this.helper.moneyValue(0),
      discountPercent: body.discount ?? 0,
      surcharge: this.helper.moneyValue(0),
      surchargePercent: body.surcharge ?? 0,
      totalTax: this.helper.moneyValue(0),
      observation: body.observation ?? '',
      imageUrls: product?.mediaUrls,
      description: product?.description,
      sapName: product?.sapName,
      stockAllocations: body.stockAllocations ?? [],
      ipi: this.helper.moneyValue(0),
      st: this.helper.moneyValue(0),
      icms: this.helper.moneyValue(0),
    };
  }

  /**
   * Converte discountRate/interestRate da condição de pagamento em ManualCondition[]
   * usando o código dedicado ZCPG (espelha DefaultCalculationService do backend).
   *
   * - discountRate > 0 → taxa positiva → classe A subtrai → preço reduzido
   * - interestRate > 0 → taxa negativa → classe A subtrai valor negativo → preço aumentado
   */
  private buildPaymentConditionManualConditions(paymentCondition?: CodedEntity): ManualCondition[] {
    if (!paymentCondition) return [];

    const conditions: ManualCondition[] = [];
    const discountRate = paymentCondition['discountRate'] ? Number(paymentCondition['discountRate']) : 0;
    const interestRate = paymentCondition['interestRate'] ? Number(paymentCondition['interestRate']) : 0;

    if (discountRate > 0) {
      conditions.push({
        condition: PAYMENT_CONDITION_CODE,
        conditionRate: discountRate,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    if (interestRate > 0) {
      conditions.push({
        condition: PAYMENT_CONDITION_CODE,
        conditionRate: -interestRate,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    return conditions;
  }

  private buildManualConditions(discount?: number, surcharge?: number): ManualCondition[] {
    const conditions: ManualCondition[] = [];

    if ((discount ?? 0) > 0) {
      conditions.push({
        condition: DISCOUNT_CONDITION_CODE,
        conditionRate: discount ?? 0,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    if ((surcharge ?? 0) > 0) {
      conditions.push({
        condition: SURCHARGE_CONDITION_CODE,
        conditionRate: surcharge ?? 0,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'B',
      });
    }

    return conditions;
  }

  private createEmptyPricingResult(): PricingResult {
    return {
      qty: 1,
      baseValue: 0,
      baseTotal: 0,
      totalValue: 0,
      finalPrice: 0,
      finalTotal: 0,
      discount: 0,
      totalDiscount: 0,
      discountPercent: 0,
      surcharge: 0,
      totalSurcharge: 0,
      surchargePercent: 0,
      tax: 0,
      totalTax: 0,
      st: 0,
      ipi: 0,
      icms: 0,
      calculationDate: new Date(),
      context: {},
      formatedPrice: this.helper.formatCurrency(0),
      formatedFinalPrice: this.helper.formatCurrency(0),
      formatedDiscount: this.helper.formatCurrency(0),
      formatedTotalDiscount: this.helper.formatCurrency(0),
      formatedSurcharge: this.helper.formatCurrency(0),
      formatedTotalSurcharge: this.helper.formatCurrency(0),
      formatedST: this.helper.formatCurrency(0),
      formatedIPI: this.helper.formatCurrency(0),
      formatedICMS: this.helper.formatCurrency(0),
      currency: 'BRL',
      steps: [],
    };
  }
}
